import { useMemo } from 'react';

import { useGetExerciseOptions } from '../redux/api/exerciseConfigAPI';
import {
  exerciseListNeedsOptionResolution,
  normalizeExerciseWithOptionRefs,
} from '../redux/api/exercises/map-available-exercises';
import { useGetAvailableDetailedExercises } from '../redux/api/exercisesAPI';
import { useGetTrainingExercisesDetailedQuery } from '../redux/api/trainingsAPI';
import { IExerciseOption } from '../types/interfaces/exercise-option';
import { orderBy, uniqBy } from '../utils/collections/array-utils';

export function useFinishingTrainingExercises(trainingId?: string) {
  const optionsState = useGetExerciseOptions();
  const resolvedExerciseOptions =
    optionsState.data ?? (optionsState.isError ? [] : undefined);
  const optionsBlocking =
    resolvedExerciseOptions === undefined &&
    !optionsState.isError &&
    (optionsState.isLoading || optionsState.isFetching);

  const {
    data: availableToUserExercises,
    isLoading: isLoadingAvailableToUserExercises,
    isFetching: isFetchingAvailableToUserExercises,
    isError: isAvailableToUserExercisesError,
  } = useGetAvailableDetailedExercises();
  const {
    data: trainingExercisesFromQuery,
    isLoading: isLoadingTrainingExercisesQuery,
    isFetching: isFetchingTrainingExercisesQuery,
    isError: isTrainingExercisesError,
  } = useGetTrainingExercisesDetailedQuery(trainingId, {
    skip: !trainingId,
  });

  const blockedByOptions =
    optionsBlocking &&
    (exerciseListNeedsOptionResolution(availableToUserExercises) ||
      exerciseListNeedsOptionResolution(trainingExercisesFromQuery));

  const normalizedAvailableExercises = useMemo(() => {
    const list = availableToUserExercises ?? [];
    return list.map((exercise) =>
      normalizeExerciseWithOptionRefs(exercise, resolvedExerciseOptions ?? []),
    );
  }, [availableToUserExercises, resolvedExerciseOptions]);

  const normalizedTrainingExercises = useMemo(() => {
    const list = trainingExercisesFromQuery ?? [];
    return list.map((exercise) =>
      normalizeExerciseWithOptionRefs(exercise, resolvedExerciseOptions ?? []),
    );
  }, [trainingExercisesFromQuery, resolvedExerciseOptions]);

  const exercises = useMemo(() => {
    if (blockedByOptions) {
      return [];
    }

    const currentTrainingIds = new Set(
      normalizedTrainingExercises.map((item) => item.id),
    );
    const merged = uniqBy(
      [...normalizedAvailableExercises, ...normalizedTrainingExercises].map(
        (exercise) => ({
          ...exercise,
          isCurrentTrainingExercise: currentTrainingIds.has(exercise.id),
        }),
      ),
      'id',
    );

    return orderBy(merged, 'name');
  }, [
    blockedByOptions,
    normalizedAvailableExercises,
    normalizedTrainingExercises,
  ]);

  const hasAvailableExercisesData =
    !isLoadingAvailableToUserExercises && !isFetchingAvailableToUserExercises;
  const hasTrainingExercisesData =
    !trainingId ||
    trainingExercisesFromQuery !== undefined ||
    (!isLoadingTrainingExercisesQuery && !isFetchingTrainingExercisesQuery);
  const shouldWaitForFirstPaint =
    !hasAvailableExercisesData && !hasTrainingExercisesData;

  const isLoadingTrainingExercises =
    !!trainingId &&
    (isLoadingTrainingExercisesQuery || isFetchingTrainingExercisesQuery);

  return {
    exercises,
    isLoading:
      blockedByOptions ||
      (shouldWaitForFirstPaint
        ? isLoadingAvailableToUserExercises ||
          isFetchingAvailableToUserExercises ||
          isLoadingTrainingExercises
        : isLoadingTrainingExercises),
    isError: isAvailableToUserExercisesError || isTrainingExercisesError,
  };
}
